import classNames from 'classnames'
import React, { ReactElement, ReactNode, useId, useState } from 'react'
import { useStableId } from '../../utils'
import { FormError } from '../form-error'
import styles from '../form-input/index.module.css'
import { IconFa } from '../icon-fa'

export interface TextAreaProps extends React.HTMLProps<HTMLTextAreaElement> {
  errorMessage?: ReactNode
  label?: string
  hideLabel?: boolean
  help?: string
  textAreaClassName?: string
  requiredText?: string
}

export function FormTextarea(props: TextAreaProps): ReactElement {
  const { id, hideLabel, help, errorMessage, textAreaClassName, requiredText = '*', label, ...rest } = props
  const stableId = useStableId()
  const errorMessageElId = useId()
  const textareaId = id ? id : stableId
  const [isFocused, setIsFocused] = useState<boolean>(false)

  const labelClassName = classNames(
    styles.label,
    Boolean(props.value) && styles.hasValue,
    props['aria-invalid'] && styles.hasError,
    props.className
  )
  const labelTextClassName = classNames(
    styles.labelText,
    props['aria-invalid'] && styles.hasError,
    isFocused && styles.focus
  )

  const onFocus = (event: React.FocusEvent<HTMLTextAreaElement, Element>) => {
    if (props.onFocus) {
      props.onFocus(event)
    }

    setIsFocused(true)
  }

  const onBlur = (event: React.FocusEvent<HTMLTextAreaElement, Element>) => {
    if (props.onBlur) {
      props.onBlur(event)
    }

    setIsFocused(false)
  }

  return (
    <div className={labelClassName}>
      {!hideLabel && (
        <label data-focused={isFocused} className={labelTextClassName} htmlFor={textareaId}>
          {label}
          {props.required && requiredText}
        </label>
      )}
      <div className="relative">
        <textarea
          id={textareaId}
          aria-describedby={errorMessageElId}
          data-focused={isFocused}
          onFocus={onFocus}
          onBlur={onBlur}
          {...rest}
          className={classNames(styles.input, help && styles.hasRightIcon, textAreaClassName)}
        />
        {help && <HelpIcon {...props} />}
      </div>

      <FormError id={errorMessageElId} showError={!!props['aria-invalid']} errorMessage={errorMessage} />
    </div>
  )
}

function HelpIcon(props: TextAreaProps) {
  return (
    <div className={classNames(styles.rightIcon, 'bottom-auto top-2')} title={props.help}>
      <IconFa icon={['far', 'info-circle']} />
    </div>
  )
}
